<template lang="html">
  <div>
    <v-card
      outlined
      :class="{
        'pb-3': !textEditor,
        'py-0 overflow-x-auto': textEditor,
        'locale-xs-screen': $vuetify.breakpoint.xsOnly,
        'locale-sm-screen': $vuetify.breakpoint.smOnly,
        'locale-md-screen': $vuetify.breakpoint.mdOnly,
        'locale-lg-screen': $vuetify.breakpoint.lgAndUp,
      }"
      class="pl-1"
      :style="{ width: '100%' }"
    >

      <!-- Language Selector for all screens -->
      <div class="mr-2 d-flex flex-grow-1">
        <Select
          :value="availableLocalesItems[currentTab]"
          :items="availableLocalesItems"
          class="flex-grow-1"
          :clearable="false"
          @change="handleLanguageChange"
        >
          <template v-slot:selection="{ item }">
            {{ $t(`languages.${item.value}`) }}
          </template>
          <template v-slot:item="{ item }">
            {{ $t(`languages.${item.value}`) }}
          </template>
        </Select>
      </div>

      <v-divider />
      <!-- Input Fields -->
      <v-tabs-items v-if="localValue" v-model="currentTab">
        <v-tab-item v-for="locale in availableLocales" :key="locale">
          <template v-if="textEditor">
            <text-editor 
              v-model="localValue[locale]" 
              class="mb-2"
              :placeholder="placeholder"
              :rules="rules"
              :required="required"
              :disabled="disabled"
              :label="label"
              :maxHeaderLevel="maxHeaderLevel"
              :disableAlign="disableAlign"
              :disableLists="disableLists"
              :disableTables="disableTables"
              :editable="!disabled"
            />
          </template>
          <template v-else>
            <Textfield 
              v-model="localValue[locale]"
              :placeholder="placeholder"
              :rules="rules"
              :required="required"
              :disabled="disabled"
              :label="label"
              :rows="rows"
              :multiLine="multiLine"
            />
          </template>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import localValue from '@/mixins/localValue';
import { mapGetters } from 'vuex';
import { findIndex } from 'lodash';

export default {
  mixins: [localValue],
  name: 'LocaleInput',
  components: {
    TextEditor: () => import('@/components/TextEditor.vue'),
  },
  props: {
    textEditor: {
      type: Boolean,
      default: false
    },
    rows: {
      type: [String, Number],
      default: 2
    },
    placeholder: {
      type: String,
      default: ''
    },
    rules: {
      type: Array,
      default: () => []
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    multiLine: {
      type: Boolean,
      default: false
    },
    // Text editor specific props
    maxHeaderLevel: {
      type: Number,
      default: 3
    },
    disableAlign: {
      type: Boolean,
      default: false
    },
    disableLists: {
      type: Boolean,
      default: false
    },
    disableTables: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentTab: undefined,
    };
  },
  mounted() {
    if (!this.localValue) this.localValue = {};
    const index = findIndex(
      this.availableLocales,
      x => x === this.currentUserLocale
    );
    if (index < 0) return;
    this.currentTab = index;
  },
  computed: {
    ...mapGetters('user', [
      'currentUserLocale',
      'availableLocales',
    ]),
    availableLocalesItems() {
      return this.availableLocales.map(locale => ({
        text: this.$t(`languages.${locale}`),
        value: locale
      }));
    },
    localeItem() {
      return this.availableLocales[this.currentTab];
    },
  },
  methods: {
    handleLanguageChange(selectedItem) {
      const index = this.availableLocales.indexOf(selectedItem);
      if (index !== -1) {
        this.currentTab = index;
      }
    },
  },
  watch: {
    localValue: {
      handler(newVal) {
        this.$emit('input', newVal);
      },
      deep: true
    }
  }
};
</script>

<style lang="css" scoped>
/deep/ p {
  margin-bottom: 0px;
}
.v-card {
  width: 100%;
}
.locale-xs-screen {
  width: 100%;
  min-width: 300px;
  overflow-x: auto !important;
}
.locale-sm-screen {
  max-width: 867px;
  overflow-x: auto !important;
}
.locale-md-screen {
  max-width: 1100px;
  overflow-x: auto !important;
}
.locale-lg-screen {
  max-width: 1540px;
  overflow-x: auto !important;
}
</style>

<style lang="scss" scoped>
.v-tab {
  max-width: 90px;
}
.v-card {
  &::v-deep .v-toolbar__content {
    @media #{map-get($display-breakpoints, 'xs-only')} {
      max-width: 300px;
      overflow-x: auto;
    }
    @media #{map-get($display-breakpoints, 'sm-only')} {
      max-width: 438px;
      width: 100%;
      overflow-x: auto;
    }
    @media #{map-get($display-breakpoints, 'md-only')} {
      max-width: 1100px;
      overflow-x: auto;
    }
    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      max-width: 1540px;
      overflow-x: auto;
    }
  }
}
</style>
